import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface AdminAlertResolutionProps {
  alertId: string;
  alertTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolved: () => void | Promise<void>;
}

export default function AdminAlertResolution({
  alertId, alertTitle, open, onOpenChange, onResolved,
}: AdminAlertResolutionProps) {
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleResolve = async () => {
    if (note.trim().length < 5) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc("admin_resolve_alert", {
        p_alert_id: alertId,
        p_resolution_note: note.trim(),
      });
      if (error) throw error;
      toast({ title: "Alert resolved" });
      setNote("");
      await onResolved();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Alert not resolved", description: `${err.message} The alert is still open.`, variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resolve Alert</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{alertTitle}</p>
        <div>
          <Label>Resolution note (required, min 5 chars)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="How was this resolved?"
            rows={3}
            disabled={submitting}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {note.trim().length < 5 ? "Write a short note (at least 5 characters) to enable Resolve." : "The alert stays in history with your note."}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleResolve} disabled={submitting || note.trim().length < 5}>
            {submitting ? "Resolving…" : "Resolve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
